import {useCallback, useEffect} from 'react';
import {useQuill} from 'react-quilljs';

export type UploadHandler = (file: File) => Promise<string | undefined>;

interface UseRichEditorOptions {
    defaultHtml?: string;
    uploadHandler?: UploadHandler;
    imageAccept?: string;
    onChange?: (html: string) => void;
}

export function useRichEditor(
    {
        defaultHtml = '',
        uploadHandler,
        imageAccept = 'image/*',
        onChange
    }: UseRichEditorOptions) {
    const {quill, quillRef} = useQuill();

    const insertToEditor = useCallback((url: string) => {
        const range = quill?.getSelection();
        if (range) quill?.insertEmbed(range.index, 'image', url);
    }, [quill]);

    const selectLocalImage = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', imageAccept);
        input.click();

        input.onchange = async () => {
            if (!input.files || input.files.length === 0) return;
            const file = input.files[0];
            if (!uploadHandler) return;
            try {
                const url = await uploadHandler(file);
                if (url) insertToEditor(url);
            } catch {
                // opcional: log/Toast
            }
        };
    }, [uploadHandler, insertToEditor, imageAccept]);

    useEffect(() => {
        if (!quill) return;

        // define html inicial se necessário
        if (defaultHtml) {
            quill.root.innerHTML = defaultHtml;
        }

        const onTextChange = () => {
            onChange && onChange(quill.root.innerHTML);
        };

        quill.on('text-change', onTextChange);

        if (uploadHandler) {
            quill.getModule('toolbar')?.addHandler('image', selectLocalImage);
        }

        return () => {
            quill.off('text-change', onTextChange);
            // não há API para remover handler do toolbar; manter selectLocalImage estável evita duplicação
        };
    }, [quill, defaultHtml, onChange, uploadHandler, selectLocalImage]);

    return {quillRef, quill, insertToEditor, selectLocalImage};
}